import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useState } from 'react';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary, ToastProvider, OfflineIndicator, Loading } from './components/common';
import { InstallPrompt, UpdateNotification } from './components/pwa';
import PerformanceDashboard from './components/performance/PerformanceDashboard';
import { SecurityProvider } from './components/security/SecurityProvider';
import { TimezoneDebug } from './components/debug';
import { useAuthStore } from './store/authStore';
import { tokenManager } from './utils/tokenManager';
import { FEATURES } from './config/features';

// Import auth pages directly (no lazy loading to avoid cache issues)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Lazy load other pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const InventoryDebugPage = lazy(() => import('./pages/InventoryDebugPage'));
const ShopDebugPage = lazy(() => import('./pages/ShopDebugPage'));
const ReadingDebugPage = lazy(() => import('./pages/ReadingDebugPage'));
const DecorationEquipmentDebugPage = lazy(() => import('./pages/DecorationEquipmentDebugPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const ReadingPage = lazy(() => import('./pages/ReadingPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReadingViewPage = lazy(() => import('./pages/ReadingViewPage'));
const TimezoneTestPage = lazy(() => import('./pages/TimezoneTestPage'));

// Import monitoring components
const PostLaunchDashboard = lazy(() => import('./components/monitoring/PostLaunchDashboard'));

// Import SharedReading directly (not lazy loaded for better sharing experience)
import { SharedReading } from './components/tarot/SharedReading';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// JWT Persistence Component - handles automatic login on app startup
function JWTPersistenceProvider({ children }: { children: React.ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const { checkAuth, isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have a valid token in localStorage
        const token = await tokenManager.getValidToken();
        
        if (token) {
          console.log('[JWT Persistence] Valid token found, attempting auto-login...');
          
          // Try to validate token with backend and get user info
          await checkAuth();
          
          if (useAuthStore.getState().isAuthenticated) {
            console.log('[JWT Persistence] Auto-login successful');
          } else {
            console.log('[JWT Persistence] Token validation failed, user needs to login');
          }
        } else {
          console.log('[JWT Persistence] No valid token found');
        }
      } catch (error) {
        console.log('[JWT Persistence] Auto-login failed:', error);
        // Clear any invalid tokens
        tokenManager.clearTokens();
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [checkAuth]);

  // Show loading screen while checking for existing authentication
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <JWTPersistenceProvider>
          <SecurityProvider 
            enableSessionTimeout={false}
            sessionTimeoutWarningMinutes={5}
          >
            <BrowserRouter>
              <ToastProvider />
              <OfflineIndicator />
              <InstallPrompt />
              <UpdateNotification />
              <PerformanceDashboard />
              {/* TimezoneDebug only in development */}
              {import.meta.env.DEV && <TimezoneDebug position="top-right" />}
              <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/shared-reading" element={<SharedReading />} />
                
                {/* Protected routes */}
                <Route 
                  path="/app" 
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Shop - Accessible for spending earned coins */}
                <Route 
                  path="/shop" 
                  element={
                    <ProtectedRoute requiresOnline={true}>
                      <ShopPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/inventory" 
                  element={
                    <ProtectedRoute requiresOnline={true}>
                      <InventoryPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/rewards" 
                  element={
                    <ProtectedRoute requiresOnline={true}>
                      <RewardsPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/reading" 
                  element={
                    <ProtectedRoute requiresOnline={true}>
                      <ReadingPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/history" 
                  element={
                    <ProtectedRoute allowOfflineAccess={true}>
                      <HistoryPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute allowOfflineAccess={true}>
                      <ProfilePage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/reading/:id" 
                  element={
                    <ProtectedRoute>
                      <ReadingViewPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Development/Admin routes */}
                {import.meta.env.DEV && (
                  <Route 
                    path="/monitoring" 
                    element={
                      <ProtectedRoute>
                        <PostLaunchDashboard />
                      </ProtectedRoute>
                    } 
                  />
                )}
                
                {/* Inventory Debug Panel - Temporarily enabled for production debugging */}
                <Route 
                  path="/inventory-debug" 
                  element={
                    <ProtectedRoute>
                      <InventoryDebugPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Shop Debug Panel - Temporarily enabled for production debugging */}
                <Route 
                  path="/shop-debug" 
                  element={
                    <ProtectedRoute>
                      <ShopDebugPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Reading Debug Panel - Temporarily enabled for production debugging */}
                <Route 
                  path="/reading-debug" 
                  element={
                    <ProtectedRoute>
                      <ReadingDebugPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Decoration Equipment Debug Panel - Temporarily enabled for production debugging */}
                <Route 
                  path="/decoration-equipment-debug" 
                  element={
                    <ProtectedRoute>
                      <DecorationEquipmentDebugPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Timezone Test Page - Development Only */}
                {import.meta.env.DEV && (
                  <Route 
                    path="/timezone-test" 
                    element={<TimezoneTestPage />} 
                  />
                )}
                
                {/* Redirect root to app */}
                <Route path="/" element={<Navigate to="/app" replace />} />
                
                {/* 404 - redirect to app */}
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Routes>
            </Suspense>
            </BrowserRouter>
          </SecurityProvider>
        </JWTPersistenceProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
